import { computed, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { useActiveModule, useModules } from '../moduleAdapter'
import { useSiteStore } from '../stores/site'
import { useI18n } from './useI18n'
import viewsEn from '../views/locales/en'
import viewsZh from '../views/locales/zh'
import setupEn from '../views/Setup/locales/en'
import setupZh from '../views/Setup/locales/zh'
import resetEn from '../views/ResetPassword/locales/en'
import resetZh from '../views/ResetPassword/locales/zh'
import notFoundEn from '../views/NotFound/locales/en'
import notFoundZh from '../views/NotFound/locales/zh'

// Base pages (admin + public auth) keep their labels in per-view locale files;
// merge them so a single `t()` can resolve every route `meta.titleKey`.
const baseMessages = {
  zh: { ...viewsZh, ...setupZh, ...resetZh, ...notFoundZh },
  en: { ...viewsEn, ...setupEn, ...resetEn, ...notFoundEn },
}

/**
 * Keeps `document.title` in sync app-wide: module pages take the title from the
 * sidebar nav label, base pages from the route `meta.titleKey`. It reacts to
 * route and language changes, so no page needs to set `document.title` itself.
 */
export function usePageTitle() {
  const route = useRoute()
  const siteStore = useSiteStore()
  const { t } = useI18n(baseMessages)

  // Module pages: match the current route against the module menu (second level first, then the entry)
  const moduleTitle = computed(() => {
    const path = route.path
    for (const module of useModules()) {
      if (!module.pathPrefixes.some((p) => path === p || path.startsWith(p + '/'))) continue
      const menu = module.getMenu()
      for (const entry of menu) {
        for (const item of entry.items ?? []) {
          if (item.path && (path === item.path || path.startsWith(item.path + '/'))) return item.label
        }
      }
      for (const entry of menu) {
        if (entry.path && (path === entry.path || path.startsWith(entry.path + '/'))) return entry.label
      }
    }
    return ''
  })

  // Public landing page keeps its own title, resolved from the active module's locales
  const landingTitle = computed(() => {
    if (route.name !== 'Landing') return ''
    return useActiveModule()?.landingTitle?.() ?? ''
  })

  const title = computed(() => {
    const key = route.meta.titleKey as string | undefined
    const label = key ? t(key) : landingTitle.value || moduleTitle.value
    return label ? `${label} · ${siteStore.displayName}` : siteStore.displayName
  })

  watchEffect(() => {
    document.title = title.value
  })
}
